import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { PANEL_SEARCH_MIN, type PanelSearchHit, type SellerTask } from "../../shared/marketStats";
import { PANEL } from "../../shared/sellerPaths";
import { useI18n } from "../i18n/I18nProvider";
import { marketApi } from "./api";
import { useInbox } from "./InboxProvider";
import { TASKS_CHANGED, taskHref, taskText } from "./SellerHome";

/** Typing somewhere already: "/" is a character there, not the shortcut. */
const typingIn = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/**
 * Mi tienda's quick search: an order (by number or buyer) or a listing (by card), straight to it.
 * "/" or Ctrl+K (⌘K) jumps to the box, unless typing somewhere else.
 */
export function PanelSearch() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const box = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [hits, setHits] = useState<PanelSearchHit[] | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    const term = text.trim();
    if (term.length < PANEL_SEARCH_MIN) return setHits(null);
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      marketApi
        .panelSearch(term, ctrl.signal)
        .then((found) => {
          setHits(found);
          setActive(-1);
        })
        .catch(() => {});
    }, 250);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [text]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ctrl+K means "delete to the end of the line" in a Mac's text fields (the order chat): not while typing either.
      const shortcut = !typingIn(e.target) && ((e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey) || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k"));
      if (!shortcut) return;
      e.preventDefault();
      box.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = (hit: PanelSearchHit) => {
    setOpen(false);
    setText("");
    setHits(null);
    box.current?.blur();
    navigate(hit.href);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const list = hits ?? [];
    if (e.key === "ArrowDown" && list.length) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % list.length);
    } else if (e.key === "ArrowUp" && list.length) {
      e.preventDefault();
      setActive((a) => (a <= 0 ? list.length - 1 : a - 1));
    } else if (e.key === "Enter" && list.length) {
      e.preventDefault();
      go(list[Math.max(active, 0)]);
    } else if (e.key === "Escape") {
      setOpen(false);
      box.current?.blur();
    }
  };
  const showing = open && hits !== null;

  return (
    <div className="sp-search" role="search">
      <label htmlFor="sp-q" className="sr-only">
        {t("sp.search")}
      </label>
      <input
        id="sp-q"
        ref={box}
        type="search"
        role="combobox"
        autoComplete="off"
        aria-expanded={showing && hits.length > 0}
        aria-controls="sp-q-list"
        aria-autocomplete="list"
        aria-activedescendant={showing && active >= 0 ? `sp-q-${active}` : undefined}
        placeholder={t("sp.searchHint")}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {showing && (
        <div className="sp-search-panel">
          {hits.length === 0 ? (
            <p className="muted small" role="status">
              {t("sp.searchNone")}
            </p>
          ) : (
            <ul id="sp-q-list" role="listbox" aria-label={t("sp.search")}>
              {hits.map((h, i) => (
                // mousedown, not click: the box's blur would close the list first.
                <li key={`${h.kind}-${h.href}-${i}`} id={`sp-q-${i}`} role="option" aria-selected={i === active} className={i === active ? "is-active" : undefined} onMouseDown={(e) => (e.preventDefault(), go(h))}>
                  <span aria-hidden="true">{h.kind === "order" ? "🧾" : "🃏"}</span>
                  <span>
                    <strong>{h.label}</strong> <span className="muted small">{h.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** The bell: how many urgent things wait (high and medium tasks), and the list, each leading to where it's done. Re-read on every page of the panel. */
export function PanelBell() {
  const { t } = useI18n();
  const { pathname, search } = useLocation();
  // Confirming a payment or answering on an order page changes the badges' counts: re-read then too.
  const { inbox } = useInbox();
  const [tasks, setTasks] = useState<SellerTask[]>([]);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const [changed, setChanged] = useState(0);
  useEffect(() => {
    const bump = () => setChanged((n) => n + 1);
    window.addEventListener(TASKS_CHANGED, bump);
    return () => window.removeEventListener(TASKS_CHANGED, bump);
  }, []);
  useEffect(() => {
    const ctrl = new AbortController();
    marketApi
      .tasks(ctrl.signal)
      .then(setTasks)
      .catch(() => {});
    return () => ctrl.abort();
  }, [pathname, search, inbox.total, changed]);
  useEffect(() => setOpen(false), [pathname, search]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onDown = (e: MouseEvent) => root.current && !root.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);
  const urgent = tasks.filter((x) => x.severity !== "low").length;
  return (
    <div className="sp-bell" ref={root}>
      <button type="button" className="icon-btn sp-bell-btn" aria-expanded={open} aria-controls="sp-bell-panel" aria-label={urgent > 0 ? t("sp.bellCount", { count: urgent }) : t("sp.bell")} onClick={() => setOpen(!open)}>
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path d="M6 16V11a6 6 0 1 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        {urgent > 0 && (
          <span className="badge badge-alert" aria-hidden="true">
            {urgent > 99 ? "99+" : urgent}
          </span>
        )}
      </button>
      {open && (
        <div id="sp-bell-panel" className="sp-bell-panel" role="region" aria-label={t("todo.title")}>
          {tasks.length === 0 ? (
            <p className="muted small">{t("todo.allDone")}</p>
          ) : (
            <ul className="todo-list">
              {tasks.map((task) => (
                <li key={task.kind} className={`todo-${task.severity}`}>
                  <Link to={taskHref(task)}>{taskText(t, task)}</Link>
                </li>
              ))}
            </ul>
          )}
          <Link to={PANEL} className="btn btn-link btn-sm">
            {t("sp.bellAll")}
          </Link>
        </div>
      )}
    </div>
  );
}
